import React, { useState, useEffect, useContext } from 'react';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getGeoLocation, submitLead, redirectToApplication } from '../services/api';
import { ModalContext } from '../App';
import './ContactUs.css';

const ContactUs = () => {
  const { openModal } = useContext(ModalContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('us');

  useEffect(() => {
    // Fetch geolocation on component mount
    const fetchGeoData = async () => {
      const geoData = await getGeoLocation();
      if (geoData && geoData.data) {
        setCountry(geoData.data.countryName || '');
        const code = (geoData.data.countryCode || 'US').toLowerCase();
        setCountryCode(code);
      }
    };
    fetchGeoData();
  }, []);

  const handlePhoneChange = (value, country) => {
    setPhone(value);
    setDialCode(country.dialCode);
    setCountryCode(country.countryCode);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Extract only numeric phone number (remove country code)
    const phoneNumber = phone.replace(/\D/g, '').replace(dialCode, '');

    const leadData = {
      name: formData.name,
      email: formData.email,
      phoneNumber: phoneNumber,
      countryDialCode: parseInt(dialCode),
      country: countryCode.toUpperCase(),
      description: `Service: ${formData.service}. Message: ${formData.message}`,
      packageTitle: formData.service,
      packageAmount: 0
    };

    const result = await submitLead(leadData);

    if (result.success) {
      // Redirect to application page
      redirectToApplication(result.customerId);
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      details: ['+1 (213) 266-5334', 'Mon-Fri, 9AM-6PM EST']
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      details: ['info@unitytrademark.com', 'support@unitytrademark.com']
    },
    {
      icon: <MapPin size={24} />,
      title: 'Office',
      details: ['2029 Century Park E', 'Los Angeles, CA 90067, USA']
    },
    {
      icon: <Clock size={24} />,
      title: 'Business Hours',
      details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday - Sunday: Closed']
    }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="page-title">Get In Touch</h1>
          <p className="page-subtitle">
            Have questions about our services? We're here to help. Reach out to us and 
            our team will respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <div className="form-header">
                <h2 className="form-title">Send Us a Message</h2>
                <p className="form-subtitle">Fill out the form below and we'll get back to you as soon as possible.</p>
                {error && (
                  <div className="form-error-message">
                    {error}
                  </div>
                )}
              </div>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="john@example.com"
                      pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                      title="Please enter a valid email address"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number *</label>
                    <PhoneInput
                      country={countryCode}
                      value={phone}
                      onChange={handlePhoneChange}
                      disabled={loading}
                      inputProps={{
                        name: 'phone',
                        required: true,
                      }}
                      containerClass="phone-input-container"
                      inputClass="phone-input-field"
                      buttonClass="phone-input-button"
                      dropdownClass="phone-input-dropdown"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="service" className="form-label">Service Interested In *</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select a service</option>
                      <option value="trademark">Trademark Registration</option>
                      <option value="copyright">Copyright Registration</option>
                      <option value="company">Company Formation</option>
                      <option value="search">Trademark Search</option>
                      <option value="amazon">Amazon Brand Registry</option>
                      <option value="other">Other/General Inquiry</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="form-textarea"
                    placeholder="Tell us about your needs..."
                  ></textarea>
                </div>

                <Button type="submit" size="lg" className="submit-button" disabled={loading}>
                  <Send size={20} />
                  {loading ? 'Submitting...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-wrapper">
              <h3 className="info-title">Contact Information</h3>
              <p className="info-subtitle">
                Reach out through any of these channels and we'll be happy to assist you.
              </p>
              <div className="contact-info-list">
                {contactInfo.map((info, index) => (
                  <div key={index} className="info-item">
                    <div className="info-icon">{info.icon}</div>
                    <div className="info-content">
                      <h4 className="info-heading">{info.title}</h4>
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="info-detail">{detail}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="cta-box">
                <h4 className="cta-box-title">Ready to Get Started?</h4>
                <p className="cta-box-text">
                  Start your trademark registration today for just $45
                </p>
                <Button 
                  className="cta-box-button" 
                  size="lg"
                  onClick={() => openModal('Trademark Registration - $45')}
                >
                  Get Started Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;